import React, { FC, useEffect, useState } from 'react';
import {
  InjectedFieldProps,
  PublicFieldProps,
  useDataProvider,
  useListContext,
  useNotify,
} from 'react-admin';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import { makeStyles } from '@material-ui/core/styles';
import { ProdPayMasterSheetInterface } from '@vatos-pas/common';

import { PermissionsProvider } from 'providers/permissionsProvider';
import { CAN_PAID_PHASE } from 'providers/permissions';

import {
  formatTotalCost,
  formatTotalCostForInput,
  parseTotalCost,
  TOTAL_COST_FAILED_MESSAGE,
  TOTAL_COST_RESOURCE,
  TOTAL_COST_SAVED_MESSAGE,
  validateTotalCost,
} from '../utils/totalCost';

/** The Prod Payables row fields the Total Cost column reads. */
export type ProdPayTotalCostRow = Pick<
  ProdPayMasterSheetInterface,
  'id' | 'isPaid' | 'costTotal'
>;

type TotalCostFieldProps = PublicFieldProps &
  InjectedFieldProps<ProdPayTotalCostRow>;

const useStyles = makeStyles({
  root: {
    minWidth: 190,
  },
  input: {
    width: 110,
  },
  value: {
    whiteSpace: 'nowrap',
  },
});

/**
 * Prod Payables MS Total Cost column: the final amount the row is paid with,
 * stored on the job phase (`costTotal`) only when Submit is chosen.
 *
 * Who can mark a row as paid can edit it (CAN_PAID_PHASE); everyone else
 * sees the stored value as text. A row marked as paid is read-only for every
 * role until its Paid? checkbox is unchecked. A blank cell means the row
 * counts with its calculated amount; zero is a value.
 */
const TotalCostField: FC<TotalCostFieldProps> = ({ record }) => {
  const classes = useStyles();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { refetch } = useListContext();
  const { hasPermission } = PermissionsProvider.useContainer();

  const stored = record?.costTotal ?? null;
  const locked = !!record?.isPaid;

  // `committed` is the stored value as the input shows it ("1250.00", blank
  // when none); `draft` diverges from it only while the user has typed
  // something not yet submitted.
  const [cell, setCell] = useState(() => {
    const committed = formatTotalCostForInput(stored);
    return { committed, draft: committed };
  });
  const [focused, setFocused] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // React-admin renders the stale row first and revalidates it, so follow
  // the server value whenever it changes, unless the user is mid-edit: an
  // unsaved draft is never discarded by a refetch. Keying on the value, not
  // on the list's loading flag, also keeps a just-saved value when the
  // follow-up reload fails and hands back the old row.
  useEffect(() => {
    const next = formatTotalCostForInput(stored);
    setCell(current => ({
      committed: next,
      draft: current.draft === current.committed ? next : current.draft,
    }));
  }, [stored]);

  // A row marked as paid is read-only: whatever was typed but not submitted
  // is dropped, so it cannot resurface submit-ready when the row is unpaid
  // again in another context. The input unmounts without blurring, so its
  // focus flag is cleared here too.
  useEffect(() => {
    if (locked) {
      setCell(current => ({ ...current, draft: current.committed }));
      setFocused(false);
    }
  }, [locked]);

  if (!record) return null;

  const { committed, draft } = cell;
  const canEdit = hasPermission(CAN_PAID_PHASE);
  const dirty = draft !== committed;
  // The inline error is about what the user typed: a stored value, legacy
  // or not, is shown exactly as recorded without one.
  const error = dirty ? validateTotalCost(draft) : null;
  const atRest = formatTotalCost(committed);

  if (!canEdit) {
    return <span className={classes.value}>{atRest}</span>;
  }

  const submit = async () => {
    if (locked || submitting || error !== null || !dirty) return;

    const costTotal = parseTotalCost(draft);
    setSubmitting(true);
    try {
      await dataProvider.update(TOTAL_COST_RESOURCE, {
        id: record.id,
        data: { costTotal },
        previousData: record,
      });
      const saved = formatTotalCostForInput(costTotal);
      setCell({ committed: saved, draft: saved });
      notify(TOTAL_COST_SAVED_MESSAGE);
      refetch();
    } catch {
      // The cell keeps what the user typed, so they can try again. The row
      // is reloaded all the same: a refusal because someone marked it as
      // paid meanwhile must show up as a locked cell, not as a retry loop.
      notify(TOTAL_COST_FAILED_MESSAGE, 'error');
      refetch();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      className={classes.root}
      display="flex"
      alignItems="flex-start"
      gridColumnGap={8}
    >
      {locked ? (
        <Box className={classes.value} pt={0.75} minWidth={110}>
          {atRest}
        </Box>
      ) : (
        <TextField
          className={classes.input}
          // Plain text, NOT type="number": a number input re-renders the
          // value in the OS locale (comma decimals) and swallows text the
          // inline validation must be able to report.
          value={focused || dirty ? draft : atRest}
          onChange={event => {
            const typed = event.target.value;
            setCell(current => ({ ...current, draft: typed }));
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          inputProps={{ inputMode: 'decimal', 'aria-label': 'Cost Override' }}
          disabled={submitting}
          error={error !== null}
          helperText={error ?? ''}
        />
      )}
      <Button
        variant="contained"
        color="primary"
        size="small"
        disabled={locked || submitting || error !== null || !dirty}
        onClick={submit}
      >
        Submit
      </Button>
    </Box>
  );
};

export default TotalCostField;
